import { Listbox, Transition } from '@headlessui/react';
import { classNames, coins } from '../../../utils/common';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';
import PropTypes from 'prop-types';

const types = {
  schedule: 'Recargas en Promoción',
  OfferProduct: 'Recargas en Promoción',
  Product: 'Recargas Básicas',
};

const priceWithCurrency = (value, currency) => `${currency === 'eur' ? value.price_eur : value.price_usd
  } ${coins.filter((coin) => coin.value === currency)[0].symbol
  }`

const Option = ({ option, currency }) => (
  <Listbox.Option
    className={({ active }) =>
      classNames(
        active ? 'bg-primary/10' : 'bg-white',
        'relative cursor-default select-none py-3 pl-4 pr-10 border-b border-gray-100 last:border-b-0',
      )
    }
    value={option}
  >
    {({ selected }) => (
      <>
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col pr-4 overflow-hidden">
            <div className="flex items-center gap-2">
              <span className="font-bold text-primary text-base truncate">
                {option.name}
              </span>
              {option.popular && (
                <span className="bg-orange-100 text-yellow-700 text-[10px] font-bold px-2 py-0.5 rounded">
                  POPULAR
                </span>
              )}
            </div>
            <div className="text-gray-500 text-sm whitespace-pre-wrap mt-0.5 leading-snug">
              {option.description}
            </div>
          </div>
          <div className="font-bold text-primary text-xl whitespace-nowrap shrink-0">
            {priceWithCurrency(option, currency)}
          </div>
        </div>

        {selected ? (
          <span className="text-primary absolute inset-y-0 right-0 flex items-center pr-3">
            <CheckIcon className="h-5 w-5" aria-hidden="true" />
          </span>
        ) : null}
      </>
    )}
  </Listbox.Option>
);

Option.propTypes = {
  option: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired
};

const Select = ({
  label,
  value,
  setValue,
  options,
  currency = 'eur',
  isLoading,
  icon,
}) => {
  // Agrupar las opciones por tipo
  const groupedOptions = options.reduce((acc, option) => {
    const groupKey = option.schedule ? 'schedule' : option.rechargeable_type;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(option);
    return acc;
  }, {});

  // Crear un arreglo de elementos que representen las opciones agrupadas
  const optionGroups = Object.entries(groupedOptions).map(([type, options]) => (
    <div className="mt-2 truncate" key={type}>
      <span className={classNames('text-gray-800 ml-3 font-bold')}>
        {types[type]}
      </span>

      {options.map((option) => (
        <Option key={`option-${option.id}-${option.type}}`} option={option} currency={currency} />
      ))}
    </div>
  ));

  return (
    <>
      <Listbox value={value} onChange={setValue}>
        {({ open }) => (
          <>
            <div className="relative">
              <Listbox.Button
                className={classNames(
                  'relative w-full cursor-default rounded-full bg-white py-1.5 pr-10 text-left ring-1 ring-inset sm:text-sm sm:leading-6',
                  isLoading || options?.length === 0
                    ? 'text-gray-600 italic'
                    : 'text-gray-900 ',
                  icon ? 'pl-10' : 'pl-3',
                )}
              >
                {icon && (
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    {icon}
                  </span>
                )}
                <span className="block truncate">
                  {options?.length > 0 &&
                    `${priceWithCurrency(value, currency)} - ${value?.name}`}
                  {isLoading
                    ? 'Cargando...'
                    : options?.length === 0 && 'No hay ofertas disponibles'}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm text-left">
                  {optionGroups}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  currency: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  icon: PropTypes.element,
};

export default Select;
